/**
 * Helper functions for AI Block activity display (formatting, status, and confidence).
 */

import _ from 'lodash'
import moment from 'moment'
import { Entity } from 'shared-libs/models/entity'
import { toObjectPath } from 'shared-libs/models/utils'
import { jsonPatchToObject } from 'shared-libs/models/types/storyboard/storyboard-utils'
import type { ChangeSet, ExtractedField } from 'shared-libs/generated/server-types/entity/blocks/aiBlockUsageEntity'
import type { GetFieldLabelFn, FieldRowData, IAiBlockActivityOptions } from './ai-block-activity-types'

export function getStatusConfig(status: string): { label: string; color: string } {
  switch (status) {
    case 'succeeded':
      return { label: 'Completed', color: 'green' }
    case 'succeeded_with_warnings':
      return { label: 'Completed with warnings', color: 'amber' }
    case 'failed':
      return { label: 'Failed', color: 'red' }
    default:
      return { label: _.startCase(status), color: 'gray' }
  }
}

export function formatRunTimestamp(isoDate: string): string {
  return moment(isoDate).tz(moment.tz.guess()).format('MMM D, YYYY [at] h:mm A')
}

export function formatDuration(durationMs: number): string {
  if (durationMs < 1000) {
    return `${durationMs}ms`
  }
  return `${(durationMs / 1000).toFixed(2)}s`
}

export function getConfidenceConfig(score: number | null | undefined): {
  color: 'green' | 'amber' | 'red' | 'none'
  label: string
  shortLabel: string
} {
  if (score == null) {
    return { color: 'none', label: 'Not available', shortLabel: '—' }
  }
  // Per spec: ≥95 green, 80-94 amber, <80 red
  if (score >= 95) {
    return { color: 'green', label: 'High confidence', shortLabel: 'High' }
  }
  if (score >= 80) {
    return { color: 'amber', label: 'Review recommended', shortLabel: 'Med' }
  }
  return { color: 'red', label: 'Low confidence', shortLabel: 'Low' }
}

export function createFieldLabelGetter(entity: Entity | null | undefined): GetFieldLabelFn {
  return (path: string): string => {
    // Try to resolve the field label from the entity schema
    if (entity) {
      try {
        // Remove leading slash for schema resolution
        const schemaPath = toObjectPath(path)
        const resolved = entity.resolveSubschemaByValuePath(schemaPath)
        if (resolved?.schema?.label) {
          return resolved.schema.label
        }
      } catch (e) {
        // If resolution fails, fall back to path parsing
      }
    }

    // Fallback: normalize to object path then split on '.' to get last segment
    const normalized = toObjectPath(path) || path
    const parts = normalized.split('.').filter(Boolean)
    if (parts.length === 0) return path
    const lastPart = parts[parts.length - 1]
    return _.startCase(lastPart)
  }
}

export function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

export function computeFieldRows(
  options: IAiBlockActivityOptions,
  changeSet?: ChangeSet,
  extractedFields?: ExtractedField[],
): FieldRowData[] {
  if (!extractedFields || extractedFields.length === 0) return []

  const applyBlob = jsonPatchToObject(changeSet?.applyPatch as [] || [])
  const revertBlob = jsonPatchToObject(changeSet?.revertPatch as [] || [])

  if (_.isEmpty(applyBlob) && _.isEmpty(revertBlob)) return []

  return extractedFields.map((field): FieldRowData => {
    const fieldPath = field.fieldPath
    const applyResult = _.get(applyBlob, fieldPath, null)
    const revertResult = _.get(revertBlob, fieldPath, null)

    const hasApply = _.has(applyBlob, fieldPath)
    const hasRevert = _.has(revertBlob, fieldPath)
    let patchOp = 'unknown'
    if (hasRevert && !hasApply) {
      patchOp = 'remove'
    } else if (hasApply && !hasRevert) {
      patchOp = 'add'
    } else if (hasApply && hasRevert) {
      patchOp = 'replace'
    }

    return patchOp === 'unknown' ? null : {
      fieldPath,
      fieldName: options.getFieldLabel(fieldPath),
      operation: patchOp,
      previousValue: revertResult != null ? formatValue(revertResult) : '—',
      currentValue: applyResult != null ? formatValue(applyResult) : '—',
      confidence: field.confidence,
      explanation: field.explanation,
    }
  }).filter((row) => !_.isEmpty(row))
}
